import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Camera, MagnifyingGlass, Check } from '@phosphor-icons/react';
import { useSession, useUser } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';

interface LinkablePhoto {
  id: number;
  sharePointFileId: string;
  label: string[];
  summary: string | null;
  buildingId: string | null;
  unitId: string | null;
  createdAt: string;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  imageFound: boolean;
}

interface Props {
  isOpen: boolean;
  ticketId: number;
  onClose: () => void;
  onLinked: () => void;
}

const PAGE_SIZE = 24;

export const RenoSelectYallyHubPhotosModal: React.FC<Props> = ({ isOpen, ticketId, onClose, onLinked }) => {
  const { session } = useSession();
  const { user } = useUser();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [photos, setPhotos] = useState<LinkablePhoto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [linking, setLinking] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Zoekterm debouncen zodat we niet bij elke toetsaanslag de backend bevragen.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchPhotos = useCallback(
    async (pageToLoad: number, replace: boolean) => {
      if (!session) return;
      replace ? setLoading(true) : setLoadingMore(true);
      try {
        const params = new URLSearchParams({
          search,
          page: String(pageToLoad),
          pageSize: String(PAGE_SIZE),
        });
        const resp = await fetchWithAuth(
          `${API_URL}/file/reno-ticket/${ticketId}/available-photos?${params}`,
          { method: 'GET' },
          session,
        );
        if (!resp.ok) throw new Error(`Fout bij ophalen foto's (${resp.status})`);
        const data: { photos: LinkablePhoto[]; totalCount: number } = await resp.json();
        setTotalCount(data.totalCount);
        setPhotos((prev) => (replace ? data.photos : [...prev, ...data.photos]));
      } catch (err) {
        console.error('RenoSelectYallyHubPhotosModal fetch:', err);
        toast.error("Kon foto's niet laden.");
      } finally {
        replace ? setLoading(false) : setLoadingMore(false);
      }
    },
    [session, ticketId, search],
  );

  // Bij openen en bij elke nieuwe zoekterm: van vooraf aan herladen.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      initializedRef.current = false;
      return;
    }
    setPage(1);
    setSelected(new Set());
    fetchPhotos(1, true);
    initializedRef.current = true;
  }, [isOpen, search, fetchPhotos]);

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchPhotos(next, false);
  };

  const handleConfirm = async () => {
    if (!session || !user?.id || selected.size === 0) return;
    setLinking(true);
    try {
      const resp = await fetchWithAuth(
        `${API_URL}/file/reno-ticket/${ticketId}/link-photos`,
        {
          method: 'POST',
          body: JSON.stringify({ fileLogIds: Array.from(selected) }),
          headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
        },
        session,
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.message || `Koppelen mislukt (${resp.status})`);
      }
      const result: { linked: number[]; failed: number[] } = await resp.json();
      if (result.linked.length > 0) {
        toast.success(
          result.linked.length === 1
            ? 'Foto gekoppeld aan het ticket.'
            : `${result.linked.length} foto's gekoppeld aan het ticket.`,
        );
      }
      if (result.failed.length > 0) {
        toast.warning(`${result.failed.length} foto('s) konden niet gekoppeld worden.`);
      }
      onLinked();
      onClose();
    } catch (err: any) {
      console.error('RenoSelectYallyHubPhotosModal link:', err);
      toast.error(err?.message || 'Koppelen mislukt.');
    } finally {
      setLinking(false);
    }
  };

  const hasMore = photos.length < totalCount;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && !linking && onClose()}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Foto's uit YallyHub koppelen</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-secondary-mid" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Zoek op bestandsnaam, label, gebouw of unit..."
            className="pl-9 text-sm"
            autoFocus
          />
        </div>

        <div className="min-h-[200px] flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-10">
              <Spinner className="h-6 w-6" />
            </div>
          ) : photos.length === 0 ? (
            <div className="py-10 text-center">
              <Camera size={32} className="text-neutral-primary/20 mx-auto mb-2" />
              <p className="text-sm text-neutral-secondary-mid">Geen foto's gevonden</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                {photos.map((photo) => {
                  const isSelected = selected.has(photo.id);
                  return (
                    <button
                      type="button"
                      key={photo.id}
                      onClick={() => toggle(photo.id)}
                      className={`group relative overflow-hidden rounded-lg border-2 transition-colors ${
                        isSelected ? 'border-moss-primary' : 'border-neutral-primary/10 hover:border-neutral-primary/30'
                      }`}
                    >
                      <div className="aspect-square w-full overflow-hidden bg-neutral-primary/5">
                        {photo.thumbnailUrl || photo.imageUrl ? (
                          <img
                            src={photo.thumbnailUrl || photo.imageUrl!}
                            alt={photo.summary || 'Foto'}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-neutral-primary/10">
                            <Camera size={24} className="text-neutral-primary/30" />
                          </div>
                        )}
                      </div>
                      {isSelected && (
                        <div className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-moss-primary text-white">
                          <Check size={12} weight="bold" />
                        </div>
                      )}
                      {(photo.buildingId || photo.summary) && (
                        <div className="truncate border-t border-neutral-primary/10 px-1.5 py-1 text-left text-[11px] text-neutral-secondary-mid">
                          {photo.buildingId || photo.summary}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {hasMore && (
                <div className="flex justify-center pt-4">
                  <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>
                    {loadingMore ? <Spinner className="h-3 w-3" /> : null}
                    Meer laden ({photos.length}/{totalCount})
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="items-center gap-2 sm:justify-between">
          <span className="text-sm text-neutral-secondary-mid">{selected.size} geselecteerd</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={linking}>
              Annuleren
            </Button>
            <Button onClick={handleConfirm} disabled={selected.size === 0 || linking}>
              {linking ? <Spinner className="h-4 w-4" /> : null}
              Koppel{selected.size > 0 ? ` (${selected.size})` : ''}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
